import defaultConfig from "./vitrine.json";

export const VITRINE_CONFIG_STORAGE_KEY = "imob-crm-vitrine:config:v1";
export const MAX_QUICK_LINKS = 4;
export const MAX_HERO_BACKGROUNDS = 4;
export const MAX_IMAGE_UPLOAD_SIZE_BYTES = 512 * 1024;
export const MAX_IMAGE_DATA_URL_LENGTH =
  "data:image/jpeg;base64,".length +
  Math.ceil(MAX_IMAGE_UPLOAD_SIZE_BYTES / 3) * 4;
export const MAX_HERO_TITLE_LENGTH = 59;
export const MAX_HERO_SUPPORTING_TEXT_LENGTH = 53;
export const MAX_COMPANY_NAME_LENGTH = 60;
export const FONT_FAMILY_OPTIONS = {
  "work-sans": '"Work Sans", sans-serif',
  arial: "Arial, sans-serif",
  georgia: "Georgia, serif",
  "trebuchet-ms": '"Trebuchet MS", sans-serif',
};

const validSectionIds = new Set(
  defaultConfig.sections.map((section) => section.id),
);
const validHeaderItemIds = new Set(
  defaultConfig.headerItems.map((item) => item.id),
);

const isValidQuickLinkUrl = (value) => {
  if (typeof value !== "string" || !value.trim() || value.length > 2048) {
    return false;
  }

  const url = value.trim();
  if (url.startsWith("/") && !url.startsWith("//")) {
    return true;
  }

  try {
    const parsedUrl = new URL(url);
    return (
      (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") &&
      Boolean(parsedUrl.hostname)
    );
  } catch {
    return false;
  }
};

const isValidImageDataUrl = (value) => {
  if (
    typeof value !== "string" ||
    value.length > MAX_IMAGE_DATA_URL_LENGTH
  ) {
    return false;
  }

  const match = value.match(
    /^data:image\/(?:jpeg|png|webp|gif);base64,([a-z\d+/]*={0,2})$/i,
  );
  if (!match || !match[1] || match[1].length % 4 !== 0) {
    return false;
  }

  const paddingLength = match[1].endsWith("==")
    ? 2
    : match[1].endsWith("=")
      ? 1
      : 0;
  const decodedSize = Math.floor((match[1].length * 3) / 4) - paddingLength;
  return decodedSize > 0 && decodedSize <= MAX_IMAGE_UPLOAD_SIZE_BYTES;
};

const isValidImageSource = (value) => {
  if (isValidImageDataUrl(value)) {
    return true;
  }

  if (typeof value !== "string" || !value.trim() || value.length > 2048) {
    return false;
  }

  const url = value.trim();
  if (url.startsWith("/") && !url.startsWith("//")) {
    return true;
  }

  try {
    const parsedUrl = new URL(url);
    return (
      (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") &&
      Boolean(parsedUrl.hostname)
    );
  } catch {
    return false;
  }
};

export const createDefaultVitrineConfig = () => ({
  version: defaultConfig.version,
  themeColor: defaultConfig.themeColor,
  secondaryColor: defaultConfig.secondaryColor,
  fontFamily: defaultConfig.fontFamily,
  showMore: defaultConfig.showMore,
  propertiesColumns: defaultConfig.propertiesColumns,
  logoUrl: defaultConfig.logoUrl,
  companyName: defaultConfig.companyName,
  heroBackgrounds: [...defaultConfig.heroBackgrounds],
  headerItems: defaultConfig.headerItems.map((item) => ({ ...item })),
  quickLinks: defaultConfig.quickLinks.map((item) => ({ ...item })),
  sections: defaultConfig.sections.map((section) => ({ ...section })),
});

export const parseVitrineConfig = (serializedConfig) => {
  const config = JSON.parse(serializedConfig);

  if (
    !config ||
    config.version !== defaultConfig.version ||
    (config.themeColor !== undefined &&
      (typeof config.themeColor !== "string" ||
        !/^#[\da-f]{6}$/i.test(config.themeColor))) ||
    (config.secondaryColor !== undefined &&
      (typeof config.secondaryColor !== "string" ||
        !/^#[\da-f]{6}$/i.test(config.secondaryColor))) ||
    (config.fontFamily !== undefined &&
      !Object.prototype.hasOwnProperty.call(
        FONT_FAMILY_OPTIONS,
        config.fontFamily,
      )) ||
    (config.showMore !== undefined && typeof config.showMore !== "boolean") ||
    (config.propertiesColumns !== undefined &&
      (!Number.isInteger(config.propertiesColumns) ||
        config.propertiesColumns < 1 ||
        config.propertiesColumns > 4)) ||
    (config.logoUrl !== undefined && !isValidImageSource(config.logoUrl)) ||
    (config.companyName !== undefined &&
      (typeof config.companyName !== "string" ||
        config.companyName.length > MAX_COMPANY_NAME_LENGTH)) ||
    (config.heroBackgrounds !== undefined &&
      (!Array.isArray(config.heroBackgrounds) ||
        config.heroBackgrounds.length > MAX_HERO_BACKGROUNDS ||
        config.heroBackgrounds.some((url) => !isValidImageSource(url)))) ||
    (config.headerItems !== undefined &&
      !Array.isArray(config.headerItems)) ||
    (config.quickLinks !== undefined &&
      (!Array.isArray(config.quickLinks) ||
        config.quickLinks.length > MAX_QUICK_LINKS)) ||
    !Array.isArray(config.sections) ||
    config.sections.length !== defaultConfig.sections.length
  ) {
    throw new Error("A configuração da vitrine tem um formato inválido.");
  }

  const sectionIds = new Set();
  const sections = config.sections.map((section) => {
    if (
      !section ||
      !validSectionIds.has(section.id) ||
      sectionIds.has(section.id) ||
      typeof section.title !== "string" ||
      !section.title.trim() ||
      section.title.length >
        (section.id === "hero" ? MAX_HERO_TITLE_LENGTH : 100) ||
      (section.visible !== undefined && typeof section.visible !== "boolean") ||
      (section.themeColor !== undefined &&
        section.themeColor !== null &&
        (typeof section.themeColor !== "string" ||
          !/^#[\da-f]{6}$/i.test(section.themeColor))) ||
      (section.backgroundColor !== undefined &&
        section.backgroundColor !== null &&
        (typeof section.backgroundColor !== "string" ||
          !/^#[\da-f]{6}$/i.test(section.backgroundColor))) ||
      (section.supportingText !== undefined &&
        (typeof section.supportingText !== "string" ||
          section.supportingText.length >
            (section.id === "hero"
              ? MAX_HERO_SUPPORTING_TEXT_LENGTH
              : 250)))
    ) {
      throw new Error("A configuração da vitrine tem um formato inválido.");
    }

    const defaultSection = defaultConfig.sections.find(
      (defaultSection) => defaultSection.id === section.id,
    );
    sectionIds.add(section.id);
    return {
      id: section.id,
      title: section.title.trim(),
      visible: section.visible !== false,
      themeColor: section.themeColor ?? null,
      backgroundColor: section.backgroundColor ?? null,
      supportingText:
        section.supportingText ?? defaultSection.supportingText,
    };
  });

  if (sectionIds.size !== validSectionIds.size) {
    throw new Error("A configuração da vitrine tem um formato inválido.");
  }

  const headerItems = config.headerItems || defaultConfig.headerItems;
  if (headerItems.length !== validHeaderItemIds.size) {
    throw new Error("A configuração da vitrine tem um formato inválido.");
  }

  const headerItemIds = new Set();
  const validatedHeaderItems = headerItems.map((item) => {
    if (
      !item ||
      !validHeaderItemIds.has(item.id) ||
      headerItemIds.has(item.id) ||
      (item.visible !== undefined && typeof item.visible !== "boolean")
    ) {
      throw new Error("A configuração da vitrine tem um formato inválido.");
    }

    headerItemIds.add(item.id);
    return { id: item.id, visible: item.visible !== false };
  });

  if (headerItemIds.size !== validHeaderItemIds.size) {
    throw new Error("A configuração da vitrine tem um formato inválido.");
  }

  const quickLinks = config.quickLinks || defaultConfig.quickLinks;
  const validatedQuickLinks = quickLinks.map((item) => {
    if (
      !item ||
      typeof item.title !== "string" ||
      !item.title.trim() ||
      item.title.length > 60 ||
      !isValidQuickLinkUrl(item.url)
    ) {
      throw new Error("A configuração da vitrine tem um formato inválido.");
    }

    return { title: item.title.trim(), url: item.url.trim() };
  });

  return {
    version: defaultConfig.version,
    themeColor: config.themeColor || defaultConfig.themeColor,
    secondaryColor: config.secondaryColor || defaultConfig.secondaryColor,
    fontFamily: config.fontFamily || defaultConfig.fontFamily,
    showMore: config.showMore ?? defaultConfig.showMore,
    propertiesColumns:
      config.propertiesColumns ?? defaultConfig.propertiesColumns,
    logoUrl: (config.logoUrl || defaultConfig.logoUrl).trim(),
    companyName: (config.companyName ?? defaultConfig.companyName).trim(),
    heroBackgrounds: [
      ...(config.heroBackgrounds ?? defaultConfig.heroBackgrounds),
    ].map((url) => url.trim()),
    headerItems: validatedHeaderItems,
    quickLinks: validatedQuickLinks,
    sections,
  };
};

export const loadVitrineConfig = () => {
  const serializedConfig = window.localStorage.getItem(
    VITRINE_CONFIG_STORAGE_KEY,
  );

  return serializedConfig
    ? parseVitrineConfig(serializedConfig)
    : createDefaultVitrineConfig();
};

export const saveVitrineConfig = (config) => {
  const validatedConfig = parseVitrineConfig(JSON.stringify(config));
  window.localStorage.setItem(
    VITRINE_CONFIG_STORAGE_KEY,
    JSON.stringify(validatedConfig),
  );

  return validatedConfig;
};

export const resetVitrineConfig = () => {
  window.localStorage.removeItem(VITRINE_CONFIG_STORAGE_KEY);
  return createDefaultVitrineConfig();
};
